import { tokenSize, seatLabelPositions } from "@/lib/tableGeometry";

// A static (non-interactive) snapshot of the guest's room: every table is drawn in
// its real position so the guest can orient themselves, but only their own table
// shows seat-level names -- the rest just show an occupancy count, since the
// backend never sends other guests' names for a public name search.
export default function MyTableMap({ layout, myName }) {
  if (!layout?.tables?.length) return null;
  const PAD = 90;
  const boxes = layout.tables.map((t) => ({ ...t, size: tokenSize(t.shape) }));
  const minX = Math.min(...boxes.map((b) => b.x)) - PAD;
  const minY = Math.min(...boxes.map((b) => b.y)) - PAD;
  const maxX = Math.max(...boxes.map((b) => b.x + b.size.w)) + PAD;
  const maxY = Math.max(...boxes.map((b) => b.y + b.size.h)) + PAD;
  const vbW = maxX - minX;
  const vbH = maxY - minY;

  return (
    <div
      className="rounded-2xl border overflow-hidden mt-4"
      style={{ borderColor: "#e3ddd2", background: "#f5f3ec" }}
      data-testid="my-table-map"
    >
      <p className="text-xs uppercase tracking-[0.2em] text-neutral-400 px-4 pt-3">{layout.room_nome}</p>
      <svg
        viewBox={`${minX} ${minY} ${vbW} ${vbH}`}
        style={{ width: "100%", height: "auto", display: "block" }}
        role="img"
        aria-label={`Disposizione della sala ${layout.room_nome}`}
      >
        {boxes.map((t) => {
          const isMine = t.id === layout.my_table_id;
          const cx = t.x + t.size.w / 2;
          const cy = t.y + t.size.h / 2;
          const seatPositions = isMine ? seatLabelPositions(t.size, t.capacity, t.rotation || 0, !!t.seats_ccw) : null;
          return (
            <g key={t.id}>
              {t.shape === "round" ? (
                <circle
                  cx={cx}
                  cy={cy}
                  r={t.size.w / 2}
                  style={isMine ? { fill: "color-mix(in srgb, var(--wed-primary) 14%, white)", stroke: "var(--wed-primary)" } : { fill: "white", stroke: "#c9c2b2" }}
                  strokeWidth={isMine ? 3 : 1.5}
                />
              ) : (
                <rect
                  x={t.x}
                  y={t.y}
                  width={t.size.w}
                  height={t.size.h}
                  rx={14}
                  style={isMine ? { fill: "color-mix(in srgb, var(--wed-primary) 14%, white)", stroke: "var(--wed-primary)" } : { fill: "white", stroke: "#c9c2b2" }}
                  strokeWidth={isMine ? 3 : 1.5}
                />
              )}
              <text x={cx} y={isMine ? cy - 4 : cy + 4} textAnchor="middle" fontSize={15} fontWeight={isMine ? 700 : 500} style={{ fill: isMine ? "var(--wed-primary)" : "#444" }}>
                {t.label}
              </text>
              {!isMine && (
                <text x={cx} y={cy + 18} textAnchor="middle" fontSize={11} fill="#9a9a9a">
                  {t.occupied}/{t.capacity}
                </text>
              )}
              {isMine &&
                seatPositions.map((pos, i) => {
                  const seat = (t.seats || []).find((s) => s.seat_index === i);
                  const mine = seat && seat.nome === myName;
                  const sx = t.x + pos.left;
                  const sy = t.y + pos.top;
                  return (
                    <g key={i}>
                      <circle
                        cx={sx}
                        cy={sy}
                        r={mine ? 15 : 10}
                        style={mine ? { fill: "var(--wed-primary)" } : { fill: seat ? "white" : "transparent", stroke: seat ? "#c9c2b2" : "transparent" }}
                        strokeWidth={1.3}
                      />
                      <text x={sx} y={sy + 4} textAnchor="middle" fontSize={mine ? 12 : 10} fontWeight={mine ? 700 : 400} fill={mine ? "white" : "#555"}>
                        {i + 1}
                      </text>
                      {seat && (
                        <text x={sx} y={sy + 28} textAnchor="middle" fontSize={mine ? 11 : 9} fontWeight={mine ? 700 : 400} style={{ fill: mine ? "var(--wed-primary)" : "#777" }}>
                          {seat.nome.split(" ")[0]}
                        </text>
                      )}
                    </g>
                  );
                })}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
